import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.jsx';
import { Trophy, Calendar, Users } from 'lucide-react';
import api from '@/lib/api';

export default function CustomerRankings() {
    const [generalRanking, setGeneralRanking] = useState([]);
    const [classRanking, setClassRanking] = useState([]);
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [loadingGeneral, setLoadingGeneral] = useState(true);
    const [loadingClass, setLoadingClass] = useState(false);
    const [weekInfo, setWeekInfo] = useState({ start: '', end: '' });

    useEffect(() => {
        const fetchGeneral = async () => {
            setLoadingGeneral(true);
            try {
                const res = await api.get('/customer/rankings/general');
                setGeneralRanking(res.data.data || []);
                setWeekInfo({ start: res.data.week_start, end: res.data.week_end });
            } catch { setGeneralRanking([]); }
            finally { setLoadingGeneral(false); }
        };

        const fetchClasses = async () => {
            try {
                const res = await api.get('/customer/classes');
                setClasses(res.data.data || []);
            } catch { setClasses([]); }
        };

        fetchGeneral();
        fetchClasses();
    }, []);

    const fetchClassRanking = async (classId) => {
        if (!classId) {
            setClassRanking([]);
            return;
        }
        setLoadingClass(true);
        try {
            const res = await api.get(`/customer/rankings/class/${classId}`);
            setClassRanking(res.data.data || []);
        } catch { setClassRanking([]); }
        finally { setLoadingClass(false); }
    };

    const handleClassChange = (classId) => {
        setSelectedClass(classId);
        fetchClassRanking(classId);
    };

    const renderRanking = (data, loading) => {
        if (loading) return <p className="text-white/60 text-center py-8">Cargando...</p>;
        if (data.length === 0) return <p className="text-white/60 text-center py-8">Aún no hay puntos registrados.</p>;

        return (
            <div className="space-y-2">
                {data.map((r, idx) => (
                    <div key={r.customer_id} className={`flex items-center justify-between p-3 rounded-lg border border-[#3a3a3f] hover:bg-white/5 ${idx === 0 ? 'bg-white/5' : ''}`}>
                        <div className="flex items-center gap-3">
                            <span className={`text-sm font-bold w-6 text-center ${idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-orange-400' : 'text-white/60'}`}>
                                {idx + 1}
                            </span>
                            <p className="font-medium text-sm text-white">{r.nombre}</p>
                        </div>
                        <span className="text-sm font-bold text-white">{r.total_points} pts</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Rankings</h1>
                <div className="flex items-center gap-2 text-sm text-white/60">
                    <Calendar className="h-4 w-4" />
                    <span>Semana: {weekInfo.start} al {weekInfo.end}</span>
                </div>
            </div>

            <Tabs defaultValue="general" className="w-full">
                <TabsList className="bg-[#0a0a0a] border border-[#3a3a3f]">
                    <TabsTrigger value="general" className="text-white data-[state=active]:bg-white/10">General</TabsTrigger>
                    <TabsTrigger value="class" className="text-white data-[state=active]:bg-white/10">Por Clase</TabsTrigger>
                </TabsList>

                <TabsContent value="general">
                    <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-white uppercase tracking-wide text-base">
                                <Trophy className="h-5 w-5 text-white" />
                                Ranking General Semanal
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {renderRanking(generalRanking, loadingGeneral)}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="class">
                    <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-white uppercase tracking-wide text-base">
                                <Users className="h-5 w-5 text-white" />
                                Ranking por Clase
                            </CardTitle>
                            <div className="mt-2">
                                <select
                                    className="w-full h-9 px-3 text-sm text-white bg-[#0a0a0a] border border-[#3a3a3f] rounded focus:outline-none focus:ring-1 focus:ring-white appearance-none cursor-pointer"
                                    value={selectedClass}
                                    onChange={e => handleClassChange(e.target.value)}
                                >
                                    <option value="" className="bg-[#0a0a0a] text-white">Seleccionar clase...</option>
                                    {classes.map(c => (
                                        <option key={c.id} value={c.gym_class?.id || c.id} className="bg-[#0a0a0a] text-white">
                                            {c.gym_class?.name || 'Clase'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {renderRanking(classRanking, loadingClass)}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
